import React from 'react';
import styled from '@emotion/styled';
import { Flex, Text } from 'src/components';
import { colors } from 'src/theme';

const RadioCardStyle = styled.div<{ isSelected: boolean; isNested?: boolean; hasGroupBorder?: boolean }>(props => {
  const showSelectionBorder = !props.hasGroupBorder && props.isSelected;
  const showDefaultShadow = !props.hasGroupBorder && !props.isSelected;
  return {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: props.isNested ? '12px' : '16px',
    borderRadius: props.isNested ? 8 : 12,
    border: showSelectionBorder ? `3px solid ${colors.primary}` : '3px solid transparent',
    backgroundColor: props.isNested && props.isSelected ? '#F5E6FF' : colors.white,
    boxShadow: showDefaultShadow ? '0 0 0 2px rgba(0, 0, 0, 0.1)' : 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',

    '&:hover': {
      border: props.hasGroupBorder ? '3px solid transparent' : `3px solid ${colors.primary}`,
      boxShadow: props.hasGroupBorder ? 'none' : 'none',
    },
  };
});

const GroupBorderWrapper = styled.div({
  borderRadius: 12,
  border: `3px solid ${colors.primary}`,
  overflow: 'hidden',
});

const RadioButton = styled.div<{ isSelected: boolean }>(props => ({
  minWidth: 20,
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: `3px solid ${props.isSelected ? colors.primary : 'rgba(0, 0, 0, 0.15)'}`,
  backgroundColor: props.isSelected ? colors.primary : 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginTop: 2,
  transition: 'all 0.2s ease',
  
  '&::after': {
    content: '""',
    width: 8,
    height: 8,
    borderRadius: '50%',
    backgroundColor: props.isSelected ? colors.white : 'transparent',
  }
}));

export interface RadioCardProps {
  title: string;
  description: string;
  isSelected: boolean;
  onClick: () => void;
  children?: React.ReactNode;
  isNested?: boolean;
  value?: string;
}

const NestedContainer = styled.div({
  position: 'relative',
  padding: '16px',
  paddingLeft: 52,
  paddingTop: 0,
  paddingBottom: 6,
  marginBottom: 10,

  '&::before': {
    content: '""',
    position: 'absolute',
    left: 26,
    top: 8,
    bottom: 8,
    width: 2,
    backgroundColor: colors.primary,
    opacity: 0.2,
  },
});

export const RadioCard: React.FC<RadioCardProps> = ({
  title,
  description,
  isSelected,
  onClick,
  children,
  isNested = false
}) => {
  const isGroupSelected = !isNested && Boolean(children) && isSelected;

  if (isGroupSelected) {
    return (
      <Flex vertical gap={12}>
        <GroupBorderWrapper>
          <RadioCardStyle isSelected={isSelected} onClick={onClick} isNested={false} hasGroupBorder>
            <RadioButton isSelected={isSelected} />
            <Flex vertical gap={4} style={{ flex: 1 }}>
              <Text type="base">{title}</Text>
              {description && (
                <Text type="small" color={colors.text4}>{description}</Text>
              )}
            </Flex>
          </RadioCardStyle>
          <NestedContainer>
            <Flex vertical gap={8}>
              {children}
            </Flex>
          </NestedContainer>
        </GroupBorderWrapper>
      </Flex>
    );
  }

  return (
    <Flex vertical gap={isNested ? 0 : 12}>
      <RadioCardStyle isSelected={isSelected} onClick={onClick} isNested={isNested}>
        <RadioButton isSelected={isSelected} />
        <Flex vertical gap={4} style={{ flex: 1 }}>
          <Text type="base">{title}</Text>
          {(isNested || isSelected) && description && (
            <Text type="small" color={colors.text4}>{description}</Text>
          )}
        </Flex>
      </RadioCardStyle>
      {isSelected && children && (
        <NestedContainer>
          <Flex vertical gap={8}>
            {children}
          </Flex>
        </NestedContainer>
      )}
    </Flex>
  );
};
